import { pure } from '@core/helpers/pure';
import { computed, inject, Injectable, Signal } from '@angular/core';
import { AccountService } from '../account/account.service';
import { OutkeepPermission } from '@outkeep/shared';
import { map, Observable, zip } from 'rxjs';
type PermissionInput = string | readonly string[];

@Injectable({
  providedIn: 'root',
})
export class OutkeepPermissionService {
  private readonly accountService = inject(AccountService);
  private readonly $currentUser = this.accountService.$currentUser;

  hasPermission$(moduleName: PermissionInput): Observable<boolean> {
    return this.accountService.currentUser$.pipe(
      map(user => this.hasAnyPermission(user?.modules, moduleName)),
    );
  }

  $hasPermission(moduleName: PermissionInput): Signal<boolean> {
    return computed(() => {
      return this.hasAnyPermission(this.$currentUser()?.modules, moduleName);
    });
  }

  private hasAnyPermission(
    modules: Record<string, unknown> | null | undefined,
    moduleName: PermissionInput,
  ): boolean {
    if (typeof moduleName === 'string') {
      return !!modules?.[moduleName];
    }

    return moduleName.some(permission => !!modules?.[permission]);
  }

  // #region простые права (модули)

  @pure
  get canLogistUseBetaOutkeep$(): Observable<boolean> {
    return this.hasPermission$(OutkeepPermission.LogistUseBetaOutkeep);
  }

  @pure
  get canLogistViewRequest$(): Observable<boolean> {
    return this.hasPermission$(OutkeepPermission.LogistViewRequest);
  }

  @pure
  get canLogistViewScheduleOwners$(): Observable<boolean> {
    return this.hasPermission$(OutkeepPermission.LogistViewScheduleOwners);
  }

  @pure
  get canLogistViewAllAsSelected$(): Observable<boolean> {
    return this.hasPermission$(OutkeepPermission.LogistViewAllAsSelected);
  }

  @pure
  get canLogistViewAllRequests$(): Observable<boolean> {
    return this.hasPermission$(OutkeepPermission.LogistViewAllRequests);
  }

  @pure
  get canLogistViewMyRequests$(): Observable<boolean> {
    return this.hasPermission$(OutkeepPermission.LogistViewMyRequests);
  }

  @pure
  get canLogistChangeRequestStatus$(): Observable<boolean> {
    return this.hasPermission$(OutkeepPermission.LogistChangeRequestStatus);
  }

  @pure
  get canLogistChangeInstaller$(): Observable<boolean> {
    return this.hasPermission$(OutkeepPermission.LogistChangeInstaller);
  }

  @pure
  get canLogistViewObd$(): Observable<boolean> {
    return this.hasPermission$(OutkeepPermission.LogistViewObd);
  }

  @pure
  get canLogistRatingSet$(): Observable<boolean> {
    return this.hasPermission$(OutkeepPermission.LogistRatingSet);
  }

  @pure
  get canLogistRatingFullAccess$(): Observable<boolean> {
    return this.hasPermission$(OutkeepPermission.LogistRatingFullAccess);
  }

  @pure
  get canLogistEditRequestsResetAddress$(): Observable<boolean> {
    return this.hasPermission$(
      OutkeepPermission.LogistEditRequestsResetAddress,
    );
  }

  @pure
  get canCompleteProducts$(): Observable<boolean> {
    return this.hasPermission$(OutkeepPermission.LogistCanCompleteProducts);
  }

  @pure
  get canLogistAddDevices$(): Observable<boolean> {
    return this.hasPermission$(OutkeepPermission.LogistAddDevices);
  }

  @pure
  get canLogistViewDeviceLink$(): Observable<boolean> {
    return this.hasPermission$(OutkeepPermission.LogistViewDeviceLink);
  }

  @pure
  get canLogistDevicesUpdateLocation$(): Observable<boolean> {
    return this.hasPermission$(OutkeepPermission.LogistDevicesUpdateLocation);
  }

  @pure
  get canViewStatus$(): Observable<boolean> {
    return this.hasPermission$(OutkeepPermission.Status);
  }

  @pure
  get canViewOtherFileGroups$(): Observable<boolean> {
    return this.hasPermission$(OutkeepPermission.LogistOtherFilesView);
  }

  @pure
  get canDownloadAlfaActs$(): Observable<boolean> {
    return this.hasPermission$(OutkeepPermission.LogisticTempActAlfaDownload);
  }

  @pure
  get canDeleteContractorFiles$(): Observable<boolean> {
    return this.hasPermission$(
      OutkeepPermission.LogistOnlyContractorFilesRemove,
    );
  }

  @pure
  get canViewComments$(): Observable<boolean> {
    return this.hasPermission$(OutkeepPermission.LogistViewComments);
  }

  @pure
  get canAddComments$(): Observable<boolean> {
    return this.hasPermission$(OutkeepPermission.LogistAddComments);
  }

  @pure
  get canDeleteComments$(): Observable<boolean> {
    return this.hasPermission$(OutkeepPermission.LogistDeleteComments);
  }

  @pure
  get canEditDealDetails$(): Observable<boolean> {
    return this.hasPermission$(OutkeepPermission.LogistEditDealDetails);
  }

  @pure
  get canSendRequestByEmail$(): Observable<boolean> {
    return this.hasPermission$(OutkeepPermission.LogistCanSendRequestByEmail);
  }

  @pure
  get canViewInstallQuality$(): Observable<boolean> {
    return this.hasPermission$(OutkeepPermission.LogistDevicesInstallQuality);
  }

  @pure
  get canViewQualityControlStatuses$(): Observable<boolean> {
    return this.hasPermission$(OutkeepPermission.LogistRatingControlView);
  }

  @pure
  get canEditQualityControlStatuses$(): Observable<boolean> {
    return this.hasPermission$(OutkeepPermission.LogistRatingControlSet);
  }

  @pure
  get canRemoveOnlyOwnerFiles$(): Observable<boolean> {
    return this.hasPermission$(OutkeepPermission.LogistOnlyOwnerFilesRemove);
  }

  @pure
  get canRemoveFiles$(): Observable<boolean> {
    return this.hasPermission$(OutkeepPermission.LogistFilesRemove);
  }

  @pure
  get canLogistViewCounterpartyAccessPointFieldInRequestCreation$(): Observable<boolean> {
    return this.hasPermission$(
      OutkeepPermission.LogistViewCounterpartyAccessPointFieldInRequestCreation,
    );
  }

  // #endregion

  // #region составные права

  @pure
  get canDeleteFiles$(): Observable<boolean> {
    return zip(this.canRemoveOnlyOwnerFiles$, this.canRemoveFiles$).pipe(
      map(
        ([logistOnlyOwnerFilesRemove, logistFilesRemove]) =>
          logistOnlyOwnerFilesRemove || logistFilesRemove,
      ),
    );
  }

  @pure
  get canViewRequestExecutor$(): Observable<boolean> {
    return zip(
      this.canLogistViewAllAsSelected$,
      this.canLogistViewAllRequests$,
    ).pipe(
      map(
        ([canLogistViewAllAsSelected, canLogistViewAllRequests]) =>
          !canLogistViewAllAsSelected ||
          (canLogistViewAllAsSelected && canLogistViewAllRequests),
      ),
    );
  }

  @pure
  get canEditRequestRatings$(): Observable<boolean> {
    return zip(this.canLogistRatingSet$, this.canLogistRatingFullAccess$).pipe(
      map(
        ([canLogistRatingSet, canLogistRatingFullAccess]) =>
          canLogistRatingSet || canLogistRatingFullAccess,
      ),
    );
  }

  // #endregion

  // #region регламенты (workflows)
  @pure
  get canViewRequestWorkflows$(): Observable<boolean> {
    return this.hasPermission$(OutkeepPermission.LogistRequestWorkflowsView);
  }

  @pure
  get canExecuteRequestWorkflowsMy$(): Observable<boolean> {
    return this.hasPermission$(
      OutkeepPermission.LogistRequestWorkflowsExecutionMy,
    );
  }

  @pure
  get canExecuteRequestWorkflowsAll$(): Observable<boolean> {
    return this.hasPermission$(
      OutkeepPermission.LogistRequestWorkflowsExecutionAll,
    );
  }

  @pure
  get canManageRequestWorkflows$(): Observable<boolean> {
    return this.hasPermission$(OutkeepPermission.LogistRequestWorkflowsManage);
  }

  @pure
  get canManageWorkflowsSetup$(): Observable<boolean> {
    return this.hasPermission$(OutkeepPermission.LogistWorkflowsSetupManage);
  }

  // #endregion регламенты (workflows)
}
